import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../store/auth';
import { useTheme } from '../store/theme';
import { notificationsApi } from '../lib/api-hooks';
import { formatRelative } from '../lib/format';
import { clsx } from 'clsx';

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { resolved, toggle } = useTheme();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [notifOpen, setNotifOpen] = useState(false);

  // Global Ctrl+K -> søk
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        navigate('/search');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  const { data: unreadData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 30_000,
  });

  const { data: notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: notificationsApi.list,
    enabled: notifOpen,
  });

  const markAllRead = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/inbox', label: 'Innboks', icon: InboxIcon },
    { to: '/mine', label: 'Mine', icon: UserIcon },
    { to: '/search', label: 'Søk', icon: SearchIcon },
    { to: '/profile', label: 'Profil', icon: ProfileIcon },
  ];

  // Desktop-only links
  const desktopLinks = [
    { to: '/kanban', label: 'Kanban' },
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/customers', label: 'Kunder' },
    { to: '/categories', label: 'Sakstyper' },
    { to: '/users', label: 'Brukere' },
  ];

  const unreadCount = unreadData?.count ?? 0;
  const isDark = resolved === 'dark';

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-slate-950">
      {/* Desktop top-bar – glassmorphism */}
      <header className="sticky top-0 z-30 hidden border-b border-slate-200/60 bg-white/80 backdrop-blur-xl dark:border-slate-800/60 dark:bg-slate-900/80 md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-brand shadow-glow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5 text-white">
                <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7" />
                <path d="M3 7l2-4h14l2 4" />
                <path d="M3 7h18" />
              </svg>
            </div>
            <span className="font-semibold text-slate-800 dark:text-slate-100">Ticket System</span>
          </div>
          <div className="flex items-center gap-3">
            <nav className="flex gap-1">
              <NavLink
                to="/inbox"
                className={({ isActive }) =>
                  clsx(
                    'rounded-lg px-3 py-1.5 text-sm font-medium transition-all',
                    isActive
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200',
                  )
                }
              >
                Innboks
              </NavLink>
              {desktopLinks.map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    clsx(
                      'rounded-lg px-3 py-1.5 text-sm font-medium transition-all',
                      isActive
                        ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'
                        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200',
                    )
                  }
                >
                  {label}
                </NavLink>
              ))}
            </nav>

            {/* Dark mode toggle */}
            <button
              onClick={toggle}
              className="btn-ghost p-2 transition-transform hover:scale-110"
              title={isDark ? 'Bytt til lys modus' : 'Bytt til mørk modus'}
            >
              {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            </button>

            {/* Varsel-klokke */}
            <div className="relative">
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                className="btn-ghost p-2 transition-transform hover:scale-110"
                title="Varsler"
              >
                <BellIcon className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] animate-bounce-subtle items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                  <div className="absolute right-0 top-full z-50 mt-2 w-80 animate-fade-in-down rounded-2xl border border-slate-200 bg-white shadow-soft-lg dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Varsler</span>
                      {unreadCount > 0 && (
                        <button
                          onClick={() => markAllRead.mutate()}
                          className="text-xs text-brand-600 hover:underline dark:text-brand-400"
                        >
                          Merk alle som lest
                        </button>
                      )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications?.length === 0 ? (
                        <div className="px-4 py-10 text-center">
                          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                            <BellIcon className="h-5 w-5 text-slate-400" />
                          </div>
                          <p className="text-sm text-slate-400">Ingen varsler</p>
                        </div>
                      ) : (
                        notifications?.map((n) => (
                          <button
                            key={n.id}
                            onClick={() => {
                              if (n.ticketId) navigate(`/ticket/${n.ticketId}`);
                              setNotifOpen(false);
                            }}
                            className={clsx(
                              'flex w-full flex-col gap-1 border-b border-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:border-slate-800/50 dark:hover:bg-slate-800/50',
                              !n.lest && 'bg-brand-50/50 dark:bg-brand-500/5',
                            )}
                          >
                            <span className="text-sm text-slate-700 dark:text-slate-200">{n.tekst}</span>
                            <span className="text-xs text-slate-400">{formatRelative(n.opprettet)}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Bruker-avatar – klikkbar, navigerer til profil */}
            <button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-2 rounded-xl p-1.5 transition-all hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Min profil"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-brand text-xs font-semibold text-white">
                {user?.navn?.charAt(0).toUpperCase() ?? '?'}
              </div>
              <span className="hidden text-sm font-medium text-slate-600 dark:text-slate-300 lg:inline">{user?.navn}</span>
            </button>
            <button onClick={handleLogout} className="btn-ghost text-sm">
              Logg ut
            </button>
          </div>
        </div>
      </header>

      {/* Hovedinnhold */}
      <main className="flex-1 pb-20 md:pb-0">
        <div className="page-enter">
          <Outlet />
        </div>
      </main>

      {/* Mobil bunn-navigasjon – glassmorphism */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 flex border-t border-slate-200/60 bg-white/90 backdrop-blur-xl pb-[env(safe-area-inset-bottom)] dark:border-slate-800/60 dark:bg-slate-900/90 md:hidden">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              clsx(
                'relative flex flex-1 flex-col items-center gap-1 py-2.5 text-xs transition-all',
                isActive
                  ? 'text-brand-600 dark:text-brand-400'
                  : 'text-slate-500 dark:text-slate-400',
              )
            }
          >
            {({ isActive }) => (
              <>
                <Icon className={clsx('h-5 w-5 transition-transform', isActive && 'scale-110')} />
                {label}
                {to === '/inbox' && unreadCount > 0 && (
                  <span className="absolute right-1/4 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function InboxIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}
function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
function ProfileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="5" />
      <path d="M20 21a8 8 0 0 0-16 0" />
    </svg>
  );
}
function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}
function MoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}
function SunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}
